import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { useCurrentUser } from "../../auth/useCurrentUser";
import { PageLoader } from "../../components/BrandedSpinner";
import { deriveConceptualRole, RESETDATA_MASTER } from "../../lib/role-labels";

/**
 * Sends the user to the right landing surface for their role.
 * CHP-520 removed the shared CM/RM/RA "Operations Core" Main Dashboard, so the
 * staff roles no longer land on /dashboard/manager:
 *   RM (ResetData Master) → /dashboard/manager/insights (Program Insights)
 *   RA (ResetData Admin)  → /dashboard/manager/deals (the staff deals queue)
 *   CM (Channel Manager)  → /dashboard/manager/deals
 *   CP (BDM/PO)           → /dashboard/partner
 * Pending users see the pending-approval message via ProtectedRoute.
 */
export function DashboardRedirector() {
  const { data, isLoading, isAuthLoading, isAuthenticated } = useCurrentUser();
  const navigate = useNavigate();

  // CHP-1105: resolve the landing surface as a value instead of returning a
  // render-time <Navigate>. A render-time <Navigate> re-navigates on every
  // re-render the transition itself causes (the CHP-1203 "Maximum update depth
  // exceeded" loop) and flashes a frame of the wrong route before it resolves.
  // Deriving one target + firing it from an effect (below) navigates exactly
  // once and shows a loader until it lands — no flicker. Mirrors ProtectedRoute.
  //
  // `null` means "no redirect" — either not decidable yet (auth/me loading) or
  // this user stays put. The two inline states (me-fetch failure, pending) are
  // NOT redirects; they contribute `null` here and render in place below.
  const redirectTarget: string | null =
    isAuthLoading || isLoading
      ? null
      : // ADR-0010: unauthenticated → /login (PRM-rendered, not Authentik).
        !isAuthenticated
        ? "/login"
        : !data
          ? null
          : data.role === "admin"
            ? // CHP-520: RM lands on Program Insights; RA lands on the Deals
              // review queue. RA is is_resetdata_master === false; RM is true
              // (or null for legacy admins seeded before the column existed →
              // default to RM).
              deriveConceptualRole({
                role: data.role,
                is_org_admin: data.is_org_admin,
                is_resetdata_master: data.is_resetdata_master,
              }) === "ra"
              ? "/dashboard/manager/deals"
              : "/dashboard/manager/insights"
            : data.role === "channel_manager"
              ? // CHP-520: CM lands on the Deals review queue (was Ops Core).
                "/dashboard/manager/deals"
              : data.role === "channel_partner"
                ? "/dashboard/partner"
                : null;

  // Fire the redirect imperatively, exactly once (ref-guarded so StrictMode's
  // double-invoke and any interim re-render can't re-fire it). `replace` keeps
  // /dashboard out of history — the user never lands back on this redirector.
  const redirected = useRef(false);
  useEffect(() => {
    if (redirectTarget === null || redirected.current) return;
    redirected.current = true;
    void navigate({ to: redirectTarget, replace: true });
  }, [redirectTarget, navigate]);

  if (isAuthLoading || isLoading) {
    return <PageLoader />;
  }

  // A pending redirect renders the loader (not a flash of the wrong route)
  // until the effect above navigates away.
  if (redirectTarget !== null) {
    return <PageLoader />;
  }

  if (!data) {
    return <div role="alert">Could not load account.</div>;
  }

  return (
    <div role="status">
      <h2>Account pending approval</h2>
      <p>A {RESETDATA_MASTER} will review your access shortly.</p>
    </div>
  );
}
